import { useQuery } from '@tanstack/react-query'
import { api } from './api'

/**
 * The artefact TEMPLATE LIBRARY, seen from the web (artefact templates 6/8, 7/8). The library is a
 * hidden bid (backend/domain/template-library.ts): its artefacts ARE the templates and its folders
 * are the template categories, at most 2 levels deep. Keep these in step with that file. Only a
 * tenant admin may open it; `GET /v1/me` hands them its bid id (`templateLibraryBidId`) since it is
 * never in a bid list. It opens in the NORMAL workspace — this module is the one place that answers
 * "is this bid the library?".
 */

/** The library's reserved bid id — mirrors backend TEMPLATE_LIBRARY_BID_ID. */
export const TEMPLATE_LIBRARY_BID_ID = '_template-library'

/** Categories stop at 2 levels — a category and a sub-category (the backend refuses a 3rd). */
export const TEMPLATE_CATEGORY_MAX_DEPTH = 2

/** The library explorer's folder depth limit — its folders are the categories. */
export const LIBRARY_MAX_FOLDER_DEPTH = TEMPLATE_CATEGORY_MAX_DEPTH

/** Is this bid the template library? An artefact there already IS a template. */
export function isTemplateLibrary(bidId: string | null): boolean {
  return bidId === TEMPLATE_LIBRARY_BID_ID
}

export function useTemplateLibrary(bid: string | null): {
  /** The library's bid id when the caller may open it (a tenant admin), else null. */
  libraryBidId: string | null
  /** The given bid IS the library. */
  isLibrary: boolean
} {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 })
  const libraryBidId = me.data?.templateLibraryBidId ?? null
  return { libraryBidId, isLibrary: libraryBidId !== null && bid === libraryBidId }
}
